'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetWinners } from '@morfi/backend-sdk-v2/client';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { GlobeButton } from '@/components/globe/globe-button';
import { WinnersHowItWorksModal } from '@/components/how-it-works/winners-how-it-works-modal';
import { type FilterDimension, type FilterValue, SortFilterBar } from '@/components/shared/sort-filter-bar';
import { AnimatedButton } from '@/components/ui/animated-button';
import type { ChipOption } from '@/components/ui/chip-multi-select';
import { EmptyState } from '@/components/ui/empty-state';
import { Bookmark02Icon, Bookmark02IconSolid, Coins01IconSolid, FilterVerticalIcon, HelpCircleIcon, Plant04IconSolid, Search02Icon, SearchRemoveIcon, UserGroup03IconSolid } from '@/components/ui/icon/glyphs';
import { categoryNameToId, DISCOVERY_CATEGORIES } from '@/config/proposal-options';
import { onEvent } from '@/events';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useAuthenticatedWatchlist } from '@/hooks/use-authenticated-watchlist';
import { useFormattedCurrency } from '@/lib/currency';
import { csvParam, dateRangeParams, dateRangeToParam, dollarsToD6, intString, paramToDateRange, paramToRange, pctToBps, type Range, rangeParams, rangeToParam } from '@/lib/filters/feed-filters';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';
import { useGraduationModalStore } from '@/stores/graduation-modal';
import { useLayoutStore } from '@/stores/layout';
import { WinnersFeed } from './winners-feed';
import { getWinnersFeedSortOrder, isWinnersFeedSortKey, resolveWinnersFeedSort, WINNERS_FEED_DEFAULT_SORT, type WinnersFeedSortKey } from './winners-feed-sorts';
const VALID_CATEGORIES: Set<string> = new Set(DISCOVERY_CATEGORIES.map(c => c.name));
const CATEGORY_CHIP_OPTIONS: ChipOption[] = DISCOVERY_CATEGORIES.map(c => ({
  value: c.name,
  label: c.name,
  emoji: c.emoji
}));

// Slider/date bounds — mirror the filter-overhaul mockup (`/tmp/morfi-filters`).
const W = {
  marketCap: {
    min: 0,
    max: 1_000_000
  },
  // ALLOW-D6-LITERAL: $1,000,000 display-dollar slider bound, not a D6 money-scale constant

  liquidity: {
    min: 0,
    max: 500_000
  },
  volume: {
    min: 0,
    max: 100_000
  },
  holders: {
    min: 0,
    max: 5000
  },
  change24h: {
    min: -90,
    max: 500
  }
} as const;
const WINNERS_GRADUATED_SPAN_DAYS = 120;
function asStrArr(value: FilterValue | undefined): string[] {
  if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
    return value as string[];
  }
  return [];
}
function asRangeVal(value: FilterValue | undefined): Range | undefined {
  if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number') {
    return [value[0], value[1] as number];
  }
  return undefined;
}
const WINNERS_FILTER_TYPE_BY_KEY: Record<string, 'category' | 'marketCap' | 'liquidity' | 'volume' | 'holders' | 'change24h' | 'graduatedAt'> = {
  category: 'category',
  marketCap: 'marketCap',
  liquidity: 'liquidity',
  volume: 'volume',
  holders: 'holders',
  change24h: 'change24h',
  graduatedAt: 'graduatedAt'
};
function handleSortEvent(value: string, previous: string): void {
  onEvent('markets_sort_changed', {
    sort: value,
    previousSort: previous
  });
}
function summariseFilterValue(value: FilterValue | undefined): string {
  if (value === undefined || value === null) return 'all';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'all';
    if (typeof value[0] === 'number') return `${value[0]}-${value[1] as number}`;
    return (value as string[]).join(',');
  }
  return 'all';
}
function buildEmptyWinnersOverhaul(): Record<string, FilterValue | undefined> {
  return {
    marketCap: [W.marketCap.min, W.marketCap.max],
    liquidity: [W.liquidity.min, W.liquidity.max],
    volume: [W.volume.min, W.volume.max],
    holders: [W.holders.min, W.holders.max],
    change24h: [W.change24h.min, W.change24h.max],
    graduatedAt: [0, 100]
  };
}
function readWinnersOverhaul(searchParams: {
  get(name: string): string | null;
}): Record<string, FilterValue | undefined> {
  return {
    marketCap: paramToRange(searchParams.get('marketCap'), W.marketCap.min, W.marketCap.max),
    liquidity: paramToRange(searchParams.get('liquidity'), W.liquidity.min, W.liquidity.max),
    volume: paramToRange(searchParams.get('volume'), W.volume.min, W.volume.max),
    holders: paramToRange(searchParams.get('holders'), W.holders.min, W.holders.max),
    change24h: paramToRange(searchParams.get('change24h'), W.change24h.min, W.change24h.max),
    graduatedAt: paramToDateRange(searchParams.get('graduatedAt'))
  };
}

// Category is owned by the global top category nav (`CategoryNav`) via the URL
// `?category=`, NOT by the overhaul state — so it is preserved straight from the
// URL in the sync effect and intentionally absent from this serialiser.
function buildWinnersUrlParams(overhaul: Record<string, FilterValue | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  const addRange = (key: string, range: Range | undefined, min: number, max: number): void => {
    if (range === undefined) return;
    const v = rangeToParam(range, min, max);
    if (v !== null) out[key] = v;
  };
  addRange('marketCap', asRangeVal(overhaul.marketCap), W.marketCap.min, W.marketCap.max);
  addRange('liquidity', asRangeVal(overhaul.liquidity), W.liquidity.min, W.liquidity.max);
  addRange('volume', asRangeVal(overhaul.volume), W.volume.min, W.volume.max);
  addRange('holders', asRangeVal(overhaul.holders), W.holders.min, W.holders.max);
  addRange('change24h', asRangeVal(overhaul.change24h), W.change24h.min, W.change24h.max);
  const grad = asRangeVal(overhaul.graduatedAt);
  if (grad !== undefined) {
    const v = dateRangeToParam(grad);
    if (v !== null) out.graduatedAt = v;
  }
  return out;
}
function replaceWinnersUrl({
  pathname,
  sortBy,
  overhaul,
  categories
}: {
  pathname: string;
  sortBy: WinnersFeedSortKey;
  overhaul: Record<string, FilterValue | undefined>;
  categories: readonly string[];
}): void {
  const params = new URLSearchParams();
  if (sortBy !== WINNERS_FEED_DEFAULT_SORT) params.set('sort', sortBy);
  if (categories.length > 0) params.set('category', categories.join(','));
  for (const [key, value] of Object.entries(buildWinnersUrlParams(overhaul))) {
    params.set(key, value);
  }
  const queryString = params.toString();
  const target = queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
  const current = `${pathname}${window.location.search}`;
  if (target !== current) {
    window.history.replaceState(null, '', target); // ALLOW-NETWORK-UNAWARE: silent same-origin filter query sync on the current path; does not navigate.
  }
}

/** Convert the winners overhaul values into winner SDK query params.
 *  Category is URL/nav-owned (not part of `overhaul`), so it's passed in
 *  separately as the live `categorySelected` names array. */
function buildWinnersArgs(overhaul: Record<string, FilterValue | undefined>, now: number, categorySelected: readonly string[]): Record<string, string> {
  return {
    // Convert category display names → canonical slugs at the wire boundary
    // (indexer stores slugs; case/name mismatch otherwise returns nothing).
    ...csvParam('category', categorySelected.map(categoryNameToId)),
    ...rangeParams(asRangeVal(overhaul.marketCap), W.marketCap.min, W.marketCap.max, 'marketCapMin', 'marketCapMax', dollarsToD6),
    ...rangeParams(asRangeVal(overhaul.liquidity), W.liquidity.min, W.liquidity.max, 'liquidityMin', 'liquidityMax', dollarsToD6),
    ...rangeParams(asRangeVal(overhaul.volume), W.volume.min, W.volume.max, 'volumeMin', 'volumeMax', dollarsToD6),
    ...rangeParams(asRangeVal(overhaul.holders), W.holders.min, W.holders.max, 'holdersMin', 'holdersMax', intString),
    ...rangeParams(asRangeVal(overhaul.change24h), W.change24h.min, W.change24h.max, 'change24hMinBps', 'change24hMaxBps', pctToBps),
    ...dateRangeParams(asRangeVal(overhaul.graduatedAt), WINNERS_GRADUATED_SPAN_DAYS, now, 'graduatedAfter', 'graduatedBefore')
  };
}
function useWinnersSortPrefetch(activeSort) {
  const $ = _c(3);
  const prefetchWinners = usePrefetchGetWinners();
  let t0;
  if ($[0] !== activeSort || $[1] !== prefetchWinners) {
    t0 = function prefetchSort(sort) {
      if (!isWinnersFeedSortKey(sort) || sort === activeSort) {
        return;
      }
      prefetchWinners({
        sortBy: sort,
        sortOrder: getWinnersFeedSortOrder(sort)
      });
    };
    $[0] = activeSort;
    $[1] = prefetchWinners;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
function buildWinnersFilterDimensions({
  labels,
  exchangeRate,
  currencySymbol
}: {
  labels: {
    category: string;
    marketCap: string;
    liquidity: string;
    volume: string;
    holders: string;
    change24h: string;
    graduatedAt: string;
    any: string;
    date: {
      any: string;
      after: string;
      before: string;
    };
  };
  exchangeRate: number;
  currencySymbol: string;
}): FilterDimension[] {
  const moneyFormat = {
    format: 'money' as const,
    anyLabel: labels.any,
    rate: exchangeRate,
    symbol: currencySymbol
  };
  return [{
    type: 'chipsMulti',
    key: 'category',
    label: labels.category,
    options: CATEGORY_CHIP_OPTIONS
  }, {
    type: 'slider',
    key: 'marketCap',
    label: labels.marketCap,
    min: W.marketCap.min,
    max: W.marketCap.max,
    format: moneyFormat
  }, {
    type: 'slider',
    key: 'liquidity',
    label: labels.liquidity,
    min: W.liquidity.min,
    max: W.liquidity.max,
    format: moneyFormat
  }, {
    type: 'slider',
    key: 'volume',
    label: labels.volume,
    min: W.volume.min,
    max: W.volume.max,
    format: moneyFormat
  }, {
    type: 'slider',
    key: 'holders',
    label: labels.holders,
    min: W.holders.min,
    max: W.holders.max,
    format: {
      format: 'int',
      anyLabel: labels.any
    }
  }, {
    type: 'slider',
    key: 'change24h',
    label: labels.change24h,
    min: W.change24h.min,
    max: W.change24h.max,
    format: {
      format: 'pct',
      signed: true,
      anyLabel: labels.any
    }
  }, {
    type: 'dateSlider',
    key: 'graduatedAt',
    label: labels.graduatedAt,
    spanDays: WINNERS_GRADUATED_SPAN_DAYS,
    labels: labels.date
  }];
}

/**
 * The standalone `/winners` page — graduated-markets winners feed with the
 * markets-style header (title + Search / Filter / How-it-works / Watchlist
 * actions), the winners sort pills (Top volume default · Newest · Most
 * traders · Biggest gainers), and the responsive `WinnersFeed` table.
 */
export function WinnersPage() {
  const $ = _c(143);
  const t = useTranslations("markets.winners");
  const tMarkets = useTranslations("markets");
  const tFilter = useTranslations("markets.filter");
  const tFilters = useTranslations("filters");
  const {
    exchangeRate,
    currencySymbol
  } = useFormattedCurrency();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [helpOpen, setHelpOpen] = useState(false);
  const openGraduation = useGraduationModalStore(_temp);
  const [filterOpen, setFilterOpen] = useState(false);
  const sortBy = resolveWinnersFeedSort(searchParams.get("sort") ?? undefined);
  const overhaul = readWinnersOverhaul(searchParams);
  let t0;
  if ($[0] !== searchParams) {
    t0 = () => searchParams.get("view") === "watchlist";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [watchlistActive, setWatchlistActive] = useState(t0);
  const categoryParam = searchParams.get("category");
  const categorySelected = categoryParam !== null ? categoryParam.split(",").filter(_temp2) : [];
  const applyCategoryToUrl = function applyCategoryToUrl(values) {
    replaceWinnersUrl({
      pathname,
      sortBy,
      overhaul,
      categories: values
    });
  };
  const [nowMs] = useState(_temp3);
  const overhaulArgs = buildWinnersArgs(overhaul, nowMs, categorySelected);
  const searchModalOpen = useLayoutStore(_temp4);
  const setSearchOpen = useLayoutStore(_temp5);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      kind: "winners",
      fields: ["pubkeys"]
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: watchlist
  } = useAuthenticatedWatchlist(t1);
  const {
    ensureAuthenticated
  } = useAuthGate();
  let t2;
  if ($[3] !== watchlist?.pubkeys) {
    t2 = watchlist?.pubkeys ?? [];
    $[3] = watchlist?.pubkeys;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const watchlistPubkeys = t2;
  const prefetchSort = useWinnersSortPrefetch(sortBy);
  let t3;
  if ($[5] !== t) {
    t3 = t("sort.newest");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = {
      label: t3,
      value: "createdAt",
      prefix: Plant04IconSolid
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("sort.topVolume");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = {
      label: t5,
      value: "volumeTotal",
      prefix: Coins01IconSolid
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("holders");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = {
      label: t7,
      value: "holderCount",
      prefix: UserGroup03IconSolid
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t6 || $[19] !== t8) {
    t9 = [t4, t6, t8];
    $[17] = t4;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const sortOptions = t9;
  let t10;
  if ($[21] !== categorySelected || $[22] !== overhaul || $[23] !== pathname) {
    t10 = function handleSortChange(next) {
      if (!isWinnersFeedSortKey(next)) {
        return;
      }
      replaceWinnersUrl({
        pathname,
        sortBy: next,
        overhaul,
        categories: categorySelected
      });
    };
    $[21] = categorySelected;
    $[22] = overhaul;
    $[23] = pathname;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleSortChange = t10;
  let t11;
  if ($[25] !== ensureAuthenticated || $[26] !== setWatchlistActive || $[27] !== watchlistActive) {
    t11 = function handleWatchlistClick() {
      if (!ensureAuthenticated()) {
        return;
      }
      const next_0 = !watchlistActive;
      setWatchlistActive(next_0);
      onEvent("markets_watchlist_toggled", {
        active: next_0
      });
    };
    $[25] = ensureAuthenticated;
    $[26] = setWatchlistActive;
    $[27] = watchlistActive;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const handleWatchlistClick = t11;
  let t12;
  if ($[29] !== applyCategoryToUrl || $[30] !== categorySelected || $[31] !== overhaul || $[32] !== pathname || $[33] !== sortBy) {
    t12 = function handleFilterChange(key, value) {
      if (key === "category") {
        const cats = asStrArr(value);
        onEvent("markets_filter_changed", {
          filterType: "category",
          value: cats.length > 0 ? cats.join(",") : "all",
          previousValue: categorySelected.length > 0 ? categorySelected.join(",") : "all"
        });
        applyCategoryToUrl(cats);
        return;
      }
      const nextOverhaul = {
        ...overhaul,
        [key]: value
      };
      replaceWinnersUrl({
        pathname,
        sortBy,
        overhaul: nextOverhaul,
        categories: categorySelected
      });
      const filterType = WINNERS_FILTER_TYPE_BY_KEY[key];
      if (filterType !== undefined) {
        onEvent("markets_filter_changed", {
          filterType,
          value: summariseFilterValue(value),
          previousValue: summariseFilterValue(overhaul[key])
        });
      }
    };
    $[29] = applyCategoryToUrl;
    $[30] = categorySelected;
    $[31] = overhaul;
    $[32] = pathname;
    $[33] = sortBy;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const handleFilterChange = t12;
  let t13;
  if ($[35] !== pathname || $[36] !== sortBy) {
    t13 = function handleClearFilters() {
      replaceWinnersUrl({
        pathname,
        sortBy,
        overhaul: buildEmptyWinnersOverhaul(),
        categories: []
      });
    };
    $[35] = pathname;
    $[36] = sortBy;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  const handleClearFilters = t13;
  let t14;
  if ($[38] !== currencySymbol || $[39] !== exchangeRate || $[40] !== t || $[41] !== tFilters) {
    t14 = buildWinnersFilterDimensions({
      labels: {
        category: t("filterCategory"),
        marketCap: t("filterMarketCap"),
        liquidity: t("filterLiquidity"),
        volume: t("filterVolume"),
        holders: t("filterHolders"),
        change24h: t("filterChange24h"),
        graduatedAt: t("filterGraduatedAt"),
        any: tFilters("any"),
        date: {
          any: tFilters("any"),
          after: tFilters("after"),
          before: tFilters("before")
        }
      },
      exchangeRate,
      currencySymbol
    });
    $[38] = currencySymbol;
    $[39] = exchangeRate;
    $[40] = t;
    $[41] = tFilters;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const winnersFilterDimensions = t14;
  let t15;
  if ($[43] !== setSearchOpen) {
    t15 = () => setSearchOpen(true);
    $[43] = setSearchOpen;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t) {
    t16 = t("searchAriaLabel");
    $[45] = t;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const t17 = searchModalOpen && "bg-background-tertiary";
  let t18;
  if ($[47] !== t17) {
    t18 = cn("text-foreground-tertiary hover:bg-background-tertiary", t17);
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t15 || $[50] !== t16 || $[51] !== t18) {
    t19 = <AnimatedButton variant="tertiary" size="default" prefix={Search02Icon} onClick={t15} aria-label={t16} className={t18} />;
    $[49] = t15;
    $[50] = t16;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <GlobeButton mode="winner" />;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setFilterOpen(true);
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t) {
    t22 = t("sortAriaLabel");
    $[55] = t;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  const t23 = filterOpen && "bg-background-tertiary";
  let t24;
  if ($[57] !== t23) {
    t24 = cn("text-foreground-tertiary hover:bg-background-tertiary", t23);
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t22 || $[60] !== t24) {
    t25 = <AnimatedButton variant="tertiary" size="default" prefix={FilterVerticalIcon} onClick={t21} aria-label={t22} className={t24} />;
    $[59] = t22;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setHelpOpen(true);
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t) {
    t27 = t("howItWorksAriaLabel");
    $[63] = t;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = helpOpen && "bg-background-tertiary";
  let t29;
  if ($[65] !== t28) {
    t29 = cn("text-foreground-tertiary hover:bg-background-tertiary", t28);
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t27 || $[68] !== t29) {
    t30 = <AnimatedButton variant="tertiary" size="default" prefix={HelpCircleIcon} onClick={t26} aria-label={t27} className={t29} />;
    $[67] = t27;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  const t31 = watchlistActive ? Bookmark02IconSolid : Bookmark02Icon;
  let t32;
  if ($[70] !== t) {
    t32 = t("watchlistAriaLabel");
    $[70] = t;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  const t33 = watchlistActive && "bg-background-tertiary";
  let t34;
  if ($[72] !== t33) {
    t34 = cn("text-foreground-tertiary hover:bg-background-tertiary", t33);
    $[72] = t33;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== handleWatchlistClick || $[75] !== t31 || $[76] !== t32 || $[77] !== t34 || $[78] !== watchlistActive) {
    t35 = <AnimatedButton variant="tertiary" size="default" prefix={t31} onClick={handleWatchlistClick} aria-label={t32} aria-pressed={watchlistActive} className={t34} />;
    $[74] = handleWatchlistClick;
    $[75] = t31;
    $[76] = t32;
    $[77] = t34;
    $[78] = watchlistActive;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== t19 || $[81] !== t25 || $[82] !== t30 || $[83] !== t35) {
    t36 = <div className="flex shrink-0 items-center gap-0">{t19}{t20}{t25}{t30}{t35}</div>;
    $[80] = t19;
    $[81] = t25;
    $[82] = t30;
    $[83] = t35;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  const headerActions = t36;
  const showEmptyWatchlist = watchlistActive && watchlistPubkeys.length === 0;
  let t37;
  if ($[85] !== t) {
    t37 = t("pageTitle");
    $[85] = t;
    $[86] = t37;
  } else {
    t37 = $[86];
  }
  let t38;
  if ($[87] !== t37) {
    t38 = <h1 className="min-w-0 text-heading font-bold">{t37}</h1>;
    $[87] = t37;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  let t39;
  if ($[89] !== headerActions || $[90] !== t38) {
    t39 = <div className="flex items-center justify-between gap-2">{t38}{headerActions}</div>;
    $[89] = headerActions;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== categorySelected || $[93] !== overhaul) {
    t40 = {
      ...overhaul,
      category: categorySelected
    };
    $[92] = categorySelected;
    $[93] = overhaul;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== tFilters) {
    t41 = tFilters("sheetTitleWinners");
    $[95] = tFilters;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== tFilters) {
    t42 = tFilters("clearAll");
    $[97] = tFilters;
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  let t43;
  if ($[99] !== tFilter) {
    t43 = tFilter("all");
    $[99] = tFilter;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] !== tFilters) {
    t44 = tFilters("close");
    $[101] = tFilters;
    $[102] = t44;
  } else {
    t44 = $[102];
  }
  let t45;
  if ($[103] !== tFilters) {
    t45 = tFilters("reset");
    $[103] = tFilters;
    $[104] = t45;
  } else {
    t45 = $[104];
  }
  let t46;
  if ($[105] !== tFilters) {
    t46 = tFilters("showResults");
    $[105] = tFilters;
    $[106] = t46;
  } else {
    t46 = $[106];
  }
  let t47;
  if ($[107] !== tFilters) {
    t47 = count => tFilters("nSelected", {
      count
    });
    $[107] = tFilters;
    $[108] = t47;
  } else {
    t47 = $[108];
  }
  let t48;
  if ($[109] !== filterOpen || $[110] !== handleClearFilters || $[111] !== handleFilterChange || $[112] !== handleSortChange || $[113] !== prefetchSort || $[114] !== sortBy || $[115] !== sortOptions || $[116] !== t40 || $[117] !== t41 || $[118] !== t42 || $[119] !== t43 || $[120] !== t44 || $[121] !== t45 || $[122] !== t46 || $[123] !== t47 || $[124] !== winnersFilterDimensions) {
    t48 = <SortFilterBar sortOptions={sortOptions} activeSort={sortBy} onSortChange={handleSortChange} onSortIntent={prefetchSort} filters={winnersFilterDimensions} activeFilters={t40} onFilterChange={handleFilterChange} onClearAll={handleClearFilters} filterSheetTitle={t41} clearAllLabel={t42} filterButtonLabel={t43} closeLabel={t44} resetLabel={t45} applyLabel={t46} selectedCountLabel={t47} onSortEvent={handleSortEvent} filterOpen={filterOpen} onFilterOpenChange={setFilterOpen} hideInlineSettings={true} />;
    $[109] = filterOpen;
    $[110] = handleClearFilters;
    $[111] = handleFilterChange;
    $[112] = handleSortChange;
    $[113] = prefetchSort;
    $[114] = sortBy;
    $[115] = sortOptions;
    $[116] = t40;
    $[117] = t41;
    $[118] = t42;
    $[119] = t43;
    $[120] = t44;
    $[121] = t45;
    $[122] = t46;
    $[123] = t47;
    $[124] = winnersFilterDimensions;
    $[125] = t48;
  } else {
    t48 = $[125];
  }
  let t49;
  if ($[126] !== overhaulArgs || $[127] !== showEmptyWatchlist || $[128] !== sortBy || $[129] !== tMarkets || $[130] !== watchlistActive || $[131] !== watchlistPubkeys) {
    t49 = showEmptyWatchlist ? <EmptyState size="page" icon={SearchRemoveIcon} title={tMarkets("noResults")} /> : <WinnersFeed sortBy={sortBy} sortOrder={getWinnersFeedSortOrder(sortBy)} {...watchlistActive ? {
      pubkeys: watchlistPubkeys
    } : {}} filterArgs={overhaulArgs} />;
    $[126] = overhaulArgs;
    $[127] = showEmptyWatchlist;
    $[128] = sortBy;
    $[129] = tMarkets;
    $[130] = watchlistActive;
    $[131] = watchlistPubkeys;
    $[132] = t49;
  } else {
    t49 = $[132];
  }
  let t50;
  if ($[133] !== openGraduation) {
    t50 = () => {
      setHelpOpen(false);
      openGraduation(true);
    };
    $[133] = openGraduation;
    $[134] = t50;
  } else {
    t50 = $[134];
  }
  let t51;
  if ($[135] !== helpOpen || $[136] !== t50) {
    t51 = <WinnersHowItWorksModal open={helpOpen} onOpenChange={setHelpOpen} onWhatIsGraduation={t50} />;
    $[135] = helpOpen;
    $[136] = t50;
    $[137] = t51;
  } else {
    t51 = $[137];
  }
  let t52;
  if ($[138] !== t39 || $[139] !== t48 || $[140] !== t49 || $[141] !== t51) {
    t52 = <div className={LISTING_INDEX_PAGE_STACK_CLASS}>{t39}{t48}{t49}{t51}</div>;
    $[138] = t39;
    $[139] = t48;
    $[140] = t49;
    $[141] = t51;
    $[142] = t52;
  } else {
    t52 = $[142];
  }
  return t52;
}
function _temp5(s_1) {
  return s_1.setSearchOpen;
}
function _temp4(s_0) {
  return s_0.searchOpen;
}
function _temp3() {
  return Date.now();
}
function _temp2(c) {
  return VALID_CATEGORIES.has(c);
}
function _temp(s) {
  return s.setOpen;
}